import {Howl, Howler} from '../../libs/howler';
import {hashName} from '../AssetList'
import { Settings } from '../Settings';
import {AudioPackCache} from './AudioPackCache';
import {VoiceModBank} from './VoiceModBank';
import {get as getModBlob} from 'idb-keyval';
import {VOICE_SLOTS} from '../../static/mod/modSchema.js';

import {
    game
} from "../Game"

Howler.autoUnlock = true;

let quality = 'lq';

let initialized = false;
const pendingSounds = new Set();
let activeVoice = null;
const maxPendingSoundAge = 5000;
const corePacks = new Set(['sfx']);

let activeSounds = {};
let activePlayingSounds = 0;
let currentQueueReleaseTime = 0;
const maxPitch = 20;
const maxPlayingSounds = 6;
const soundQueueReleaseInterval = 50;
// Portal feedback must get through when ordinary impact effects have filled
// the sound budget.
const prioritySounds = new Set([
	'portal_shoot', 'portal_open', 'portal_close', 'portal_reject', 'portal_buzz_loop',
]);
const maxPendingPortalSounds = 32;

const getPlaybackRate = (pitch = 1, gameSpeed = game.editor.editorSettingsObject.gameSpeed) =>
	Math.min(maxPitch, Math.max(0.1, pitch * gameSpeed));

import sfxJSON from '../../static/assets/audio/sfx-hq.json';
import pack1JSON from '../../static/assets/audio/pack1-hq.json';
import pack2JSON from '../../static/assets/audio/pack2-hq.json';
import portalJSON from '../../static/assets/audio/portal-hq.json';
import laserJSON from '../../static/assets/audio/laser-hq.json';
import skateJSON from '../../static/assets/audio/skate-hq.json';
import hankJSON from '../../static/assets/audio/voice-hank-hq.json';
import mrskatJSON from '../../static/assets/audio/voice-mrskat-hq.json';
import bobzombieJSON from '../../static/assets/audio/voice-bobzombie-hq.json';
import billyjoelJSON from '../../static/assets/audio/voice-billyjoel-hq.json';
import jeroenJSON from '../../static/assets/audio/voice-jeroen-hq.json';
import mariqueJSON from '../../static/assets/audio/voice-marique-hq.json';
import damienJSON from '../../static/assets/audio/voice-damien-hq.json';
import seanbroJSON from '../../static/assets/audio/voice-seanbro-hq.json';
import coljacksonJSON from '../../static/assets/audio/voice-coljackson-hq.json';
import brittanyJSON from '../../static/assets/audio/voice-brittany-hq.json';
import xenotJSON from '../../static/assets/audio/voice-xenot-hq.json';
import rondaJSON from '../../static/assets/audio/voice-ronda-hq.json';
import jackleeJSON from '../../static/assets/audio/voice-jacklee-hq.json';
import thezuckJSON from '../../static/assets/audio/voice-thezuck-hq.json';
import crashyJSON from '../../static/assets/audio/voice-crashy-hq.json';
import machoteJSON from '../../static/assets/audio/voice-machote-hq.json';

let audioSpriteKeys = [...Object.keys(pack1JSON.sprite), ...Object.keys(pack2JSON.sprite)].sort();
const packs = {
	sfx: sfxJSON, pack1: pack1JSON, pack2: pack2JSON, portal: portalJSON, laser: laserJSON, skate: skateJSON,
	'voice-hank': hankJSON, 'voice-mrskat': mrskatJSON, 'voice-bobzombie': bobzombieJSON,
	'voice-billyjoel': billyjoelJSON, 'voice-jeroen': jeroenJSON, 'voice-marique': mariqueJSON,
	'voice-damien': damienJSON, 'voice-seanbro': seanbroJSON, 'voice-coljackson': coljacksonJSON,
	'voice-brittany': brittanyJSON, 'voice-xenot': xenotJSON, 'voice-ronda': rondaJSON,
	'voice-jacklee': jackleeJSON, 'voice-thezuck': thezuckJSON, 'voice-crashy': crashyJSON, 'voice-machote': machoteJSON,
};
const spritePackMap = new Map();
for (const [name, json] of Object.entries(packs)) {
	for (const sprite of Object.keys(json.sprite)) spritePackMap.set(sprite, name);
}

const audioPacks = new AudioPackCache(name => new Howl({
	src: (packs[name].formats || ['ogg', 'mp3']).map(format =>
		`assets/audio/${hashName(`${name}-${quality}.${format}`)}`),
	sprite: packs[name].sprite,
	preload: false,
	autoplay: false,
}));

const voiceMods = new VoiceModBank({readBlob: getModBlob, createHowl: options => new Howl(options)});
export const setVocalsDisabled = disabled => {
	Settings.disableVocals = disabled;
	if (disabled) activeVoice?.stop();
};
export const setVoiceMods = keys => {
	activeVoice?.stop();
	voiceMods.replace(keys);
};
// Until the player interacts with the page the browser keeps audio suspended,
// and Howler would queue every sound to play them all at once afterwards.
export const audioLocked = () => Howler.usingWebAudio === true && Howler.ctx?.state !== 'running' &&
	globalThis.navigator?.userActivation?.hasBeenActive === false;
export const unlockAudio = () => {
	if (Howler.ctx && Howler.ctx.state !== 'running') Howler._autoResume();
};
export const hasCharacterVoice = sprite => voiceMods.has(sprite) || spritePackMap.get(sprite)?.startsWith('voice-') === true;
export const preloadCharacterVoice = slug => Promise.all(Object.keys(VOICE_SLOTS)
	.filter(slot => hasCharacterVoice(`${slug}_${slot}`)).map(slot => preloadSFX(`${slug}_${slot}`)));

// The replay video export waits for every sound before its first frame.
export const loadSounds = slug => Promise.all([
	...['sfx', 'pack1', 'pack2', 'portal', 'laser', 'skate'].map(name => audioPacks.load(name)),
	slug && preloadCharacterVoice(slug),
]);

export const init = ()=>{
	initialized = true;
	// Extra packs are fetched only when a level or editor requests their sounds.
	if (Settings.sfxOn) return audioPacks.load('sfx');
}

export const getAvailableAudioSprites = ()=>{
	return audioSpriteKeys;
}

export const preloadSFX = sprite => {
	if(!initialized || !Settings.sfxOn) return;
	if(Settings.disableVocals && hasCharacterVoice(sprite)) return;
	const builtin = () => spritePackMap.has(sprite) ? audioPacks.load(spritePackMap.get(sprite)) : null;
	if (voiceMods.has(sprite)) return voiceMods.load(sprite).then(howl => howl || builtin());
	return builtin();
}

// One local-player voice at a time, outside the impact-effect budget. Use the
// same game-speed scaling as SFX, with steady volume as the camera moves.
export const playCharacterVoice = (sprite, volume = 0.65, interrupt = false, {gameSpeed} = {}) => {
	if (!initialized || !Settings.sfxOn || Settings.disableVocals || audioLocked() || !hasCharacterVoice(sprite)) return null;
	const modded = voiceMods.has(sprite);
	const howl = modded ? voiceMods.get(sprite) : getHowl(sprite);
	if (!howl) {
		preloadSFX(sprite);
		return null;
	}
	if (activeVoice?.playing()) {
		if (!interrupt) return null;
		activeVoice.stop();
	}
	const id = modded ? howl.play() : howl.play(sprite);
	if (typeof id !== 'number') return null;
	howl.volume(volume, id);
	howl.rate(getPlaybackRate(1, gameSpeed), id);
	howl.stereo(0, id);
	let stopped = false;
	const handle = {
		playing: () => !stopped && howl.playing(id),
		stop: () => {
			if (!stopped) howl.stop(id);
			stopped = true;
			if (activeVoice === handle) activeVoice = null;
		},
	};
	activeVoice = handle;
	return handle;
}

const getHowl = sprite => {
	return audioPacks.get(spritePackMap.get(sprite));
}

const determineVolumeAndPan = (pos, ignoreDistance = false) => {
	if(!pos) return {vl:1.0, pan:0};

	const effectMargin = 300;
	let pixiPoint = game.editor.getPIXIPointFromWorldPoint(pos);
	// In Pixi 8 the level is a child of the camera, so toGlobal already
	// includes its transform. Applying it again can mute onscreen vehicles.
	pixiPoint = game.editor.container.toGlobal(pixiPoint);

	const dx = window.innerWidth/2 - pixiPoint.x;
	const dy = window.innerHeight/2 - pixiPoint.y;
	const distance = Math.sqrt(dx*dx + dy*dy);

	let maxDistance = window.innerWidth > window.innerHeight ? window.innerWidth/2 : window.innerHeight/2;
	maxDistance += effectMargin;

	const vl = ignoreDistance ? 1 : Math.max(0, 1-(distance / maxDistance)) || 0;
	const pan = Math.max(-1, Math.min(1, (-dx / maxDistance) || 0));

	return {vl, pan}
}

export const playPrefabUniqueLoopSFX = (prefabName, sfxName, volume, pitch=1, position) => {
	if(!initialized || !Settings.sfxOn || audioLocked() || !spritePackMap.has(sfxName)) return;
	const priority = prioritySounds.has(sfxName);
	if(!priority && activePlayingSounds>=maxPlayingSounds) return;

	pitch = getPlaybackRate(pitch);
	const {vl, pan} = determineVolumeAndPan(position);
	if(vl === 0){
		stopPrefabUniqueLoopSFX(prefabName, sfxName);
		return;
	}
	const howl = getHowl(sfxName);
	if (!howl) {
		// Vehicle loops are requested each frame. Wait for the next request
		// instead of building up a playback queue while the pack downloads.
		audioPacks.load(spritePackMap.get(sfxName));
		return;
	}

	let soundId = null;
	if(activeSounds[prefabName] && activeSounds[prefabName][sfxName]){
		soundId = activeSounds[prefabName][sfxName];
	}else {
		soundId = howl.play(sfxName);
		howl.loop(true, soundId);
		if(!activeSounds[prefabName]) activeSounds[prefabName] = {};
		activeSounds[prefabName][sfxName] = soundId;
	}

	howl.volume(volume*vl, soundId);
	howl.rate(pitch, soundId);
	howl.stereo(pan, soundId);
}
export const stopPrefabUniqueLoopSFX = (prefabName, sfxName) => {
	const howl = getHowl(sfxName);
	if(!howl) return;

	let soundId = null;
	if(activeSounds[prefabName] && activeSounds[prefabName][sfxName]){
		soundId = activeSounds[prefabName][sfxName];
	}
	if(soundId){
		howl.loop(false, soundId);
		try{
			howl.stop(soundId);
		}catch(e){
			//
		}
		delete activeSounds[prefabName][sfxName];
	}
}
export const playSFX = (sfxName, volume, pitch=1, position, ignoreDistance = false) => {

	if(Array.isArray(sfxName)) sfxName = sfxName[Math.floor(Math.random() * sfxName.length)];

	if(!initialized || !Settings.sfxOn || audioLocked() || !spritePackMap.has(sfxName)) return;
	const priority = prioritySounds.has(sfxName);
	if(!priority && activePlayingSounds>=maxPlayingSounds) return;

	pitch = getPlaybackRate(pitch);

	const {vl, pan} = determineVolumeAndPan(position, ignoreDistance);
	if(vl === 0) return;

	const play = howl => {
		const soundId = howl.play(sfxName);
		howl.volume(volume*vl, soundId);
		howl.rate(pitch, soundId);
		howl.stereo(pan, soundId);
		if(!priority) activePlayingSounds++;
		return soundId;
	};
	const howl = getHowl(sfxName);
	if (howl) return play(howl);
	// Keep slow downloads bounded while allowing portal cues through a full
	// ordinary queue (for example a close/open pair during an explosion).
	if (pendingSounds.size >= (priority ? maxPendingPortalSounds : maxPlayingSounds)) return;

	// Return a cancellable handle while loading so e.g. the skippyball can
	// still stop its charge sound before the audio is ready. Capture volume
	// and pan now; physics positions may no longer exist when loading ends.
	const pending = {cancelled: false, howl: null, id: null};
	const expires = Date.now() + maxPendingSoundAge;
	pendingSounds.add(pending);
	audioPacks.load(spritePackMap.get(sfxName)).then(loaded => {
		pendingSounds.delete(pending);
		if (!loaded || pending.cancelled || !Settings.sfxOn || Date.now() > expires ||
			(!priority && activePlayingSounds >= maxPlayingSounds)) return;
		pending.howl = loaded;
		pending.id = play(loaded);
	});
	return pending;
}
export const stopSFX = (sfxName, id) => {
	if (id && typeof id === 'object') {
		id.cancelled = true;
		pendingSounds.delete(id);
		if (id.howl && id.id !== null) id.howl.stop(id.id);
		return;
	}
	const howl = getHowl(sfxName);
	if(!howl) return;
	try{
		howl.stop(id)
	} catch(e){
		//
	}
}
export const stopAllSounds = ()=>{
	activeVoice?.stop();
	pendingSounds.forEach(pending => { pending.cancelled = true; });
	pendingSounds.clear();
	try{
		Object.keys(activeSounds).forEach( key => {
			const innerKeys = Object.keys(activeSounds[key]);
			if(innerKeys){
				innerKeys.forEach(innerKey => {
					const howl = getHowl(innerKey);
					if(howl){
						const soundId = activeSounds[key][innerKey];
						howl.loop(false, soundId);
						howl.stop(soundId);
					}
				})
			}
		});

		audioPacks.stop();
		// Pause/mute drops waiting optional packs; a current decode is freed
		// on completion. Loaded sounds stay ready for a quick resume.
		audioPacks.releaseExcept(corePacks, true);
	}catch(e){
		//
	}
	activeSounds = {}
	activePlayingSounds = 0;
	currentQueueReleaseTime = 0;
}
export const releaseOptionalAudio = () => {
	stopAllSounds();
	audioPacks.releaseExcept(corePacks);
	voiceMods.release();
}
export const update = ()=> {
	currentQueueReleaseTime += game.editor.deltaTime;

	const released = Math.floor(currentQueueReleaseTime / soundQueueReleaseInterval);
	activePlayingSounds = Math.max(0, activePlayingSounds - released);
	currentQueueReleaseTime %= soundQueueReleaseInterval;
}
